import { Suspense } from 'react'
import { useNavigate } from 'react-router-dom'
import { JSONTree } from 'react-json-tree'
import { connect } from 'react-redux'

import { Box, Sx } from '@dhl/theme'
import { useKeyToggle } from '~/util/hooks/useKeyToggle'
import { Basket } from '~/basket/model/reducers'
import {
  confirmStep,
  updatePackage,
  UpdatePackageOptions,
  updateReceiver,
  updateShipper,
  UpdateReceiverOptions,
  updateCustoms,
  UpdateShipperOptions,
} from '~/basket/model/session/actions'
import { ReduxState, selectors } from '~/redux/createRootReducer'
import { Receiver, Package, Customs, Shipper } from '~/basket/model/session/reducers'
import { STEP_NAME } from '~/basket/model/constants'
import { links } from '~/links'

const classes: Record<string, Sx> = {
  debugger: {
    position: 'fixed',
    zIndex: 9999,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'scroll',
    backgroundColor: 'rgb(0, 43, 54)',
  },
  menu: {
    listStyleType: 'none',
    display: 'flex',
    padding: 0,
  },
  item: (theme) => ({
    margin: theme.spacing(1),
    padding: theme.spacing(1),
    border: 'rgb(133, 153, 0) 1px solid',
    color: 'rgb(38, 139, 210)',
    cursor: 'pointer',
  }),
}

const receiver: Partial<Receiver> = {
  firstName: 'Jan',
  lastName: 'de Groot',
  address: {
    countryCode: 'NL',
    postalCode: '3542AB',
    street: 'Atoomweg',
    houseNumber: '30',
    houseNumberSuffix: '',
    city: 'Utrecht',
    suburb: '',
    additionalAddressLine: '',
  },
  email: 'jan@dhl.com',
  phoneNumber: '0612345678',
  deliveryOptionKey: 'DOOR',
}

const shipper: Partial<Shipper> = {
  firstName: 'Piet',
  lastName: 'de Lange',
  address: {
    countryCode: 'NL',
    postalCode: '3542AB',
    street: 'Oude Gracht',
    houseNumber: '30',
    houseNumberSuffix: '',
    city: 'Utrecht',
    suburb: '',
    additionalAddressLine: '',
  },
  email: 'piet@dhl.com',
  phoneNumber: '0687654321',
}

type Props = {
  basket: Basket
  confirmStep: (stepName: string) => void
  updatePackage: ($package: Partial<Package>, options: UpdatePackageOptions) => void
  updateReceiver: (receiver: Partial<Receiver>, options: UpdateReceiverOptions) => void
  updateShipper: (shipper: Partial<Shipper>, options: UpdateShipperOptions) => void
  // eslint-disable-next-line react/no-unused-prop-types
  updateCustoms: (customs: Partial<Customs>) => void
}

export const BasketDebuggerComponent = ({
  basket,
  // eslint-disable-next-line @typescript-eslint/no-shadow
  confirmStep,
  // eslint-disable-next-line @typescript-eslint/no-shadow
  updatePackage,
  // eslint-disable-next-line @typescript-eslint/no-shadow
  updateReceiver,
  // eslint-disable-next-line @typescript-eslint/no-shadow
  updateShipper,
}: Props) => {
  const navigate = useNavigate()
  const isOpen = useKeyToggle('Escape')
  const isAllowed = window.location.href.indexOf('localhost') >= 0

  const goToShipper = () => {
    updatePackage({ destinationCountry: 'NL' }, { hasUpdatedDestinationCountry: true })
    updatePackage({ parcelTypeKey: 'SMALL' }, { hasUpdatedParcelTypeKey: true })
    confirmStep(STEP_NAME.PACKAGE)
    updateReceiver(receiver, { hasUpdatedPostalCode: true })
    confirmStep(STEP_NAME.RECEIVER)

    navigate(links.shipmentShipperLink())
  }

  const goToCustoms = () => {
    updatePackage({ destinationCountry: 'NO' }, { hasUpdatedDestinationCountry: true })
    confirmStep(STEP_NAME.PACKAGE)
    updateReceiver(receiver, { hasUpdatedPostalCode: true })
    confirmStep(STEP_NAME.RECEIVER)
    updateShipper(shipper, { hasUpdatedPostalCode: true })
    confirmStep(STEP_NAME.SHIPPER)

    navigate(links.shipmentCustomsLink())
  }

  const goToOverview = () => {
    updatePackage({ destinationCountry: 'NL' }, { hasUpdatedDestinationCountry: true })
    updatePackage({ parcelTypeKey: 'SMALL' }, { hasUpdatedParcelTypeKey: true })
    confirmStep(STEP_NAME.PACKAGE)
    updateReceiver(receiver, { hasUpdatedPostalCode: true })
    confirmStep(STEP_NAME.RECEIVER)
    updateShipper(shipper, { hasUpdatedPostalCode: true })
    confirmStep(STEP_NAME.SHIPPER)

    navigate(links.shipmentOverviewLink())
  }

  if (isOpen && isAllowed) {
    return (
      <Suspense fallback="Loading basket debugger...">
        <Box sx={classes.debugger}>
          <Box component="ul" sx={classes.menu}>
            <Box component="li" onClick={goToShipper} sx={classes.item}>
              Shipper
            </Box>

            {basket.selectableDestinationCountries.some(({ code }) => code === 'NO') && (
              <Box component="li" onClick={goToCustoms} sx={classes.item}>
                Customs
              </Box>
            )}

            <Box component="li" onClick={goToOverview} sx={classes.item}>
              Overview
            </Box>
          </Box>

          <JSONTree data={basket} />
        </Box>
      </Suspense>
    )
  }

  return null
}

const mapStateToProps = (state: ReduxState) => ({
  basket: selectors.getBasket(state),
})

const mapDispatchToProps = {
  confirmStep,
  updatePackage,
  updateReceiver,
  updateShipper,
  updateCustoms,
}

export const BasketDebugger = connect(mapStateToProps, mapDispatchToProps)(BasketDebuggerComponent)
